/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { useReducer } from 'preact/hooks';
import type { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { timeReducer } from './timeReducer';

import type { TimeGranularity, TimePlaceholders } from './types';
import { TimeField } from './TimeField';
import { getTimeFieldInitialState } from './getTimeFieldInitialState';
import { useTimeAriaLabels } from './useTimeAriaLabels';

type TimeFieldType = ComponentProps<typeof TimeField>;

type UseTimeFieldProps = {
  granularity: TimeGranularity;
  hasLeadingZeroForHour: boolean;
  locale: BCP47Locale;
  timeMasks: TimePlaceholders;
  value?: TimeFieldType['value'];
};

/**
 * Hook that initializes the time state by calling useReducer.
 * It returns a state and a dispatch method to request changes to that state.
 *
 * **Important**: Ensure that the `timeMasks` include all placeholders required by the specified `granularity`.
 * For example, if `granularity` is "second",
 * the `timeMasks` must include the second placeholder. Since 'InputTimeMask' does this validation,
 * it is not done here in the hook for efficiency sake.
 * @param {Object} props - Configuration properties for the hook.
 * @param {TimePlaceholders} props.timeMasks - The time placeholders representing the structure of the time format.
 * e.g.,
 * [ { type: 'hour', value: 'hh' }, { type: 'literal', value: ':' }, { type: 'minute', value: 'mm' }, { type: 'literal', value: ' ' }, { type: 'dayPeriod', value: 'am/pm' } ]
 * @param {TimeGranularity} props.granularity - Specifies the smallest time unit that is displayed by the component.
 * @param {BCP47Locale} props.locale - The locale used for formatting the time.
 * @param {boolean} [props.hasLeadingZeroForHour] - Whether to show a leading zero in the hour field when there is only one digit or not. E.g., 01:00 AM vs 1:00 AM.
 * @param {Time | undefined} [props.value] - The current time value.
 * @returns An object containing the current 'state' and a 'dispatch' function.
 */
const useTimeFieldState = ({
  granularity,
  hasLeadingZeroForHour,
  locale,
  timeMasks,
  value: currentValue
}: UseTimeFieldProps) => {
  const ariaLabels = useTimeAriaLabels({ locale });

  const [state, dispatch] = useReducer(
    timeReducer,
    {
      ariaLabels,
      currentValue,
      hasLeadingZeroForHour,
      granularity,
      locale,
      timeMasks
    },
    getTimeFieldInitialState
  );

  return { dispatch, state };
};

export { useTimeFieldState };
